/**
 * Product Card Quick Add Button
 * Triggers the existing quick-view functionality
 * - Llama directamente al método del custom element <quick-view>
 * - Reutiliza toda la lógica ya implementada en el tema
 * - Detecta el swatch activo para mostrar la variante correcta
 * - Verifica que el drawer existe antes de proceder
 */

class ProductCardQuickAdd extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.productHandle = this.button?.dataset.productHandle;
    
    // Find product card container
    this.productCard = this.closest('product-card') || this.closest('.product-card');
    
    if (this.button && this.productCard) {
      this.button.addEventListener('click', this.handleClick.bind(this));
      this.initSwatchTracking();
    }
  }
  
  /**
   * Initialize swatch tracking to update variant display
   */
  initSwatchTracking() {
    const swatchesContainer = this.productCard.querySelector('.product-card-swatches--scrollable');
    if (!swatchesContainer) return;
    
    const swatches = swatchesContainer.querySelectorAll('.product-card-swatch');
    
    // Listen for swatch hover events to update active state
    swatches.forEach(swatch => {
      swatch.addEventListener('mouseenter', () => {
        // Remove active class from all swatches
        swatches.forEach(s => s.classList.remove('active'));
        // Add active class to hovered swatch
        swatch.classList.add('active');
      });
    });
  }
  
  /**
   * Handle button click - trigger quick view
   * Uses multiple strategies to ensure the quick-view opens correctly
   */
  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    
    // Prevent double-click/tap on mobile
    if (this.button.disabled || this.button.classList.contains('loading')) {
      return;
    }
    
    // Verify that the Product Drawer exists in the DOM
    const drawer = document.getElementById('Product-Drawer');
    if (!drawer) {
      console.error('Product drawer (#Product-Drawer) not found in DOM');
      // Fallback: navigate to product page only if drawer doesn't exist
      if (this.productHandle) {
        window.location.href = `/products/${this.productHandle}`;
      }
      return;
    }
    
    // Find the quick-view element in this product card
    let quickViewElement = this.productCard?.querySelector('quick-view');
    
    // If quick-view not found in product-card, try to find it in parent containers
    // This handles cases like product-card-small in cart drawer
    if (!quickViewElement) {
      // Search in parent elements (for cart drawer recommendations)
      let parent = this.productCard?.parentElement;
      while (parent && !quickViewElement) {
        quickViewElement = parent.querySelector(`quick-view[data-product-handle="${this.productHandle}"]`);
        if (!quickViewElement) {
          parent = parent.parentElement;
        } else {
          break;
        }
      }
    }
    
    // If still not found, create a temporary quick-view element
    if (!quickViewElement && this.productHandle) {
      quickViewElement = document.createElement('quick-view');
      quickViewElement.setAttribute('data-product-handle', this.productHandle);
      quickViewElement.classList.add('product-card-quickview', 'text-button');
      // Append to body temporarily (will be removed after use)
      document.body.appendChild(quickViewElement);
    }
    
    if (quickViewElement) {
      // Verify it has the required data attribute
      if (!quickViewElement.dataset.productHandle && this.productHandle) {
        quickViewElement.setAttribute('data-product-handle', this.productHandle);
      }
      
      if (!quickViewElement.dataset.productHandle) {
        console.warn('Quick view element missing product handle');
        // Only redirect if we can't proceed
        if (this.productHandle) {
        window.location.href = `/products/${this.productHandle}`;
        }
        return;
      }
      
      // Show loading state on our button
      this.button.classList.add('loading');
      
      // Strategy 1: Try to call the method directly (most reliable)
      if (typeof quickViewElement.setupEventListener === 'function') {
        // Create a synthetic event to pass to the method
        const syntheticEvent = new Event('click', {
          bubbles: true,
          cancelable: true
        });
        
        // Call the method directly
        try {
        quickViewElement.setupEventListener(syntheticEvent);
        } catch (error) {
          console.error('Error calling setupEventListener:', error);
          // Fallback to dispatch event
          quickViewElement.dispatchEvent(syntheticEvent);
        }
        
      } else {
        // Strategy 2: Dispatch a MouseEvent (more complete than simple click)
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          view: window,
          detail: 1
        });
        
        quickViewElement.dispatchEvent(clickEvent);
      }
      
      // Remove loading state after a delay
      // The quick-view element will show its own loading state
      setTimeout(() => {
        this.button.classList.remove('loading');
        // Clean up temporary quick-view element if we created it
        if (quickViewElement.parentElement === document.body && !quickViewElement.closest('.product-card')) {
          quickViewElement.remove();
        }
      }, 500);
      
    } else {
      console.warn('Could not create or find quick view element');
      // Last resort: navigate to product page
      if (this.productHandle) {
        window.location.href = `/products/${this.productHandle}`;
      }
    }
  }
}

// Register custom element
if (!customElements.get('quick-add-button')) {
  customElements.define('quick-add-button', ProductCardQuickAdd);
}

// Re-initialize on dynamic content load (Shopify theme editor, filters, etc)
document.addEventListener('shopify:section:load', () => {
  console.log('Quick add buttons reinitialized on section load');
});

// Debug helper (remove in production)
if (window.location.search.includes('debug')) {
  console.log('Quick add button custom element registered');
}
